import { Component, OnInit } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { EmailTemplateService } from '../services/email-template.service';
import { EmailTemplate, EmailBranding, SuccessModal } from '../app.model';
import { EditEmailTemplateComponent } from '../components/edit-email-template/edit-email-template.component';
import { SuccessModalComponent } from '../components/success-modal/success-modal.component';
import { DataSharedService } from '../services/data-shared.service';
import { CATEGORIES, CATEGORY_ORDER, metaFor } from './email-template-meta';

interface TemplateGroup {
  key: string;
  label: string;
  icon: string;
  class: string;
  templates: EmailTemplate[];
}

@Component({
  selector: 'app-email-templates',
  templateUrl: './email-templates.component.html',
  styleUrls: ['./email-templates.component.css']
})
export class EmailTemplatesComponent implements OnInit {

  loading = false;
  templates: EmailTemplate[] = [];
  branding: EmailBranding = {
    primary_color: '#0566EA',
    background_color: '#FFFFFF',
    button_color: '#0566EA',
  };
  brandingSaving = false;
  brandingSaved = false;
  brandingDirty = false;
  private originalBranding: EmailBranding = { ...this.branding };

  // List view state
  searchQuery = '';
  collapsed: { [key: string]: boolean } = {};
  editedOnlyFilter = false; // Phase 2 — UI hidden until API returns is_customized

  agencyName = ''; // real agency name for the email previews

  constructor(
    private emailTemplateService: EmailTemplateService,
    private modalService: NgbModal,
    private dataSharedService: DataSharedService,
  ) { }

  ngOnInit(): void {
    this.loadData();
  }

  loadData() {
    this.loading = true;
    this.emailTemplateService.getTemplates().subscribe((res: any) => {
      this.templates = res.data;
      this.loading = false;
    }, () => { this.loading = false; });

    this.emailTemplateService.getBranding().subscribe((res: any) => {
      this.branding = {
        primary_color: res.primary_color || '#0566EA',
        background_color: res.background_color || '#FFFFFF',
        button_color: res.button_color || '#0566EA',
      };
      this.originalBranding = { ...this.branding };
      this.brandingDirty = false;
    });

    this.dataSharedService.getCompanyDetails().subscribe((res: any) => {
      this.agencyName = res?.agency_details?.[0]?.name || '';
    }, () => { });
  }

  // ---------- Grouping + filtering ----------
  get filteredGroups(): TemplateGroup[] {
    const q = this.searchQuery.trim().toLowerCase();

    const filtered = this.templates.filter(t => {
      if (this.editedOnlyFilter && !this.isCustomised(t)) return false;
      if (!q) return true;
      const catLabel = CATEGORIES[metaFor(t.name).category]?.label || '';
      return (t.label || '').toLowerCase().includes(q)
        || (t.subject || '').toLowerCase().includes(q)
        || catLabel.toLowerCase().includes(q);
    });

    return CATEGORY_ORDER.map(key => {
      const cat = CATEGORIES[key];
      return {
        key,
        label: cat.label,
        icon: cat.icon,
        class: cat.class,
        templates: filtered.filter(t => metaFor(t.name).category === key),
      };
    }).filter(g => g.templates.length > 0);
  }

  get totalCount(): number {
    return this.filteredGroups.reduce((n, g) => n + g.templates.length, 0);
  }

  // Phase 2: wire to API `is_customized`. No edited state available yet.
  isCustomised(_t: EmailTemplate): boolean {
    return false;
  }

  toggleGroup(key: string) {
    this.collapsed[key] = !this.collapsed[key];
  }

  clearFilters() {
    this.searchQuery = '';
    this.editedOnlyFilter = false;
  }

  // ---------- Branding ----------
  onBrandingChange() {
    this.brandingDirty =
      this.branding.primary_color !== this.originalBranding.primary_color ||
      this.branding.background_color !== this.originalBranding.background_color ||
      this.branding.button_color !== this.originalBranding.button_color;
  }

  saveBranding() {
    this.brandingSaving = true;
    this.brandingSaved = false;
    this.emailTemplateService.setBranding(this.branding).subscribe(() => {
      this.brandingSaving = false;
      this.brandingSaved = true;
      this.originalBranding = { ...this.branding };
      this.brandingDirty = false;
      setTimeout(() => this.brandingSaved = false, 3000);
    }, () => {
      this.brandingSaving = false;
    });
  }

  discardBranding() {
    this.branding = { ...this.originalBranding };
    this.brandingDirty = false;
  }

  // ---------- Row actions ----------
  editTemplate(template: EmailTemplate) {
    console.log("Edit Template");
    const modalRef: NgbModalRef = this.modalService.open(EditEmailTemplateComponent, {
      size: 'lg',
      centered: true,
      keyboard: false,
      backdrop: 'static',
      windowClass: 'tpl-edit-window',
    });
    // Set branding + agencyName BEFORE template — the template setter triggers
    // the modal's first preview render, which reads these.
    modalRef.componentInstance.branding = { ...this.branding };
    modalRef.componentInstance.agencyName = this.agencyName;
    modalRef.componentInstance.template = { ...template };
    modalRef.result.then((updated: EmailTemplate | null) => {
      if (updated) {
        const idx = this.templates.findIndex(t => t.uuid === updated.uuid);
        if (idx !== -1) this.templates[idx] = updated;
        this.successModal('Template saved');
      }
    }, () => { });
  }

  resetTemplate(template: EmailTemplate) {
    this.emailTemplateService.resetTemplate(template.uuid).subscribe((res: any) => {
      const updated = res.data;
      const idx = this.templates.findIndex(t => t.uuid === updated.uuid);
      if (idx !== -1) this.templates[idx] = updated;
      this.successModal('Template reset to default');
    });
  }

  private successModal(message: string) {
    const modalRef = this.modalService.open(SuccessModalComponent, {
      size: 'sm',
      modalDialogClass: 'success-modal',
      backdrop: false,
    });
    const data: SuccessModal = {
      message,
      image: 'assets/img/checkmark.png',
      duration: 2000,
    };
    modalRef.componentInstance.data = data;
  }
}
